import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Loader2, Star } from 'lucide-react';

export const EditLLCDialog = ({ open, onOpenChange, formData, setFormData, onSubmit, formLoading }) => {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="font-serif text-xl">Edit LLC</DialogTitle>
          <DialogDescription>Update LLC information</DialogDescription>
        </DialogHeader>
        <form onSubmit={onSubmit}>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="edit-name">LLC Name</Label>
              <Input id="edit-name" value={formData.name || ''} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-type">Type</Label>
              <Select value={formData.llc_type} onValueChange={(value) => setFormData({ ...formData, llc_type: value })}>
                <SelectTrigger><SelectValue placeholder="Select type" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="real_estate">Real Estate Property</SelectItem>
                  <SelectItem value="cash_balance">Liquidating Cash Balance</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {formData.llc_type === 'real_estate' && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="edit-address">Property Address</Label>
                  <Input id="edit-address" value={formData.property_address || ''} onChange={(e) => setFormData({ ...formData, property_address: e.target.value })} />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-description">Property Description</Label>
                  <Textarea id="edit-description" value={formData.property_description || ''} onChange={(e) => setFormData({ ...formData, property_description: e.target.value })} rows={3} />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-appraised">Appraised Value</Label>
                  <Input id="edit-appraised" type="number" step="0.01" min="0" value={formData.appraised_value || ''} onChange={(e) => setFormData({ ...formData, appraised_value: e.target.value ? parseFloat(e.target.value) : null })} placeholder="0.00" data-testid="edit-appraised-value" />
                </div>
              </>
            )}
            <div className="space-y-2">
              <Label htmlFor="edit-status">Status</Label>
              <Select value={formData.status} onValueChange={(value) => setFormData({ ...formData, status: value })}>
                <SelectTrigger><SelectValue placeholder="Select status" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                  <SelectItem value="liquidating">Liquidating</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center space-x-2 pt-2">
              <Checkbox
                id="edit-hide-percentages"
                checked={formData.hide_percentages || false}
                onCheckedChange={(checked) => setFormData({ ...formData, hide_percentages: checked })}
                data-testid="edit-hide-percentages-checkbox"
              />
              <Label htmlFor="edit-hide-percentages" className="text-sm font-normal cursor-pointer">
                Hide ownership percentages from viewers
              </Label>
            </div>
            <div className="flex items-center space-x-2 pt-2">
              <Checkbox
                id="edit-issue-financials"
                checked={formData.issue_financials !== false}
                onCheckedChange={(checked) => setFormData({ ...formData, issue_financials: !!checked })}
                data-testid="edit-issue-financials-checkbox"
              />
              <Label htmlFor="edit-issue-financials" className="text-sm font-normal cursor-pointer">
                Issue financial statements (include in Quarterly Reports email)
              </Label>
            </div>
            <div className={`flex items-center space-x-2 pt-2 px-2 py-2 rounded-md transition-colors ${formData.info_current ? 'bg-amber-50' : ''}`}>
              <button
                type="button"
                onClick={() => setFormData({ ...formData, info_current: !formData.info_current })}
                className="focus:outline-none"
                data-testid="edit-info-current-star"
              >
                <Star className={`w-6 h-6 transition-colors ${formData.info_current ? 'fill-amber-400 text-amber-400' : 'fill-slate-200 text-slate-300'}`} />
              </button>
              <Label className="text-sm font-normal cursor-pointer" onClick={() => setFormData({ ...formData, info_current: !formData.info_current })}>
                LLC information is current and verified
              </Label>
            </div>
            <div className={`flex items-center space-x-2 pt-2 px-2 py-2 rounded-md transition-colors ${formData.distributions_current ? 'bg-blue-50' : ''}`}>
              <button
                type="button"
                onClick={() => setFormData({ ...formData, distributions_current: !formData.distributions_current })}
                className="focus:outline-none"
                data-testid="edit-distributions-current-star"
              >
                <Star className={`w-6 h-6 transition-colors ${formData.distributions_current ? 'fill-blue-400 text-blue-400' : 'fill-slate-200 text-slate-300'}`} />
              </button>
              <Label className="text-sm font-normal cursor-pointer" onClick={() => setFormData({ ...formData, distributions_current: !formData.distributions_current })}>
                All distributions and admin is current
              </Label>
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" className="bg-slate-900 hover:bg-slate-800" disabled={formLoading}>
              {formLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save Changes'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default EditLLCDialog;
